// Pure session-timer logic for Chat.jsx. Extracted into its own module so
// the grace-period rules are unit-testable without a React renderer. The
// chat screen drives the UI off `computeSessionTimerPhase`'s return shape
// once per second; the same function is called from the load effect to
// rehydrate the correct phase on refresh (or back-navigation) given only
// the session's startedAt and the configured time limit.
//
// CommonJS on purpose: react-scripts/Babel handles `import { … }` from
// CommonJS modules transparently, and the server test suite (jest in
// node-CJS mode) can require this file via a relative path without any
// jest config change.

// Five-minute grace window after the displayed timer reaches zero. The
// child sees an amber banner ("you have 5 minutes to finish your thought")
// and can keep typing; if they don't end the session manually, the chat
// screen seals it automatically when the grace runs out.
const DEFAULT_GRACE_SECONDS = 300;

// Threshold at which the banner is forced visible again even after the
// child has dismissed it — gives them a final heads-up that the session
// is about to seal.
const DEFAULT_BANNER_REAPPEAR_SECONDS = 60;

/**
 * Derives the current phase of the session timer from the server-truth
 * startedAt timestamp and the configured time limit. Pure — never reads
 * Date.now() itself; the caller passes nowMs.
 *
 * @param {object} params
 * @param {number} params.startedAtMs    — epoch-ms the session began
 * @param {number} params.timeLimitMinutes — child's per-session cap (parent-set)
 * @param {number} params.nowMs          — current wall clock in epoch-ms
 * @param {number} [params.backgroundMs] — total ms the PWA has been
 *   backgrounded since the session started. Subtracted from elapsed
 *   so a child who puts the device down for 20 minutes doesn't lose
 *   those 20 minutes from their session allowance. Clamped at zero.
 * @param {number} [params.graceSeconds] — override grace window (test convenience)
 * @returns {{
 *   phase: 'active' | 'grace' | 'expired',
 *   secondsLeft: number,
 *   graceSecondsLeft: number,
 *   bannerForcedVisible: boolean,
 * }}
 *   - phase: where in the lifecycle the session sits right now.
 *   - secondsLeft: main timer remaining (0 once we enter grace).
 *   - graceSecondsLeft: grace remaining (= graceSeconds while active,
 *     ticks down inside grace, 0 once expired).
 *   - bannerForcedVisible: true when the grace window has dropped under
 *     the reappear threshold (default 60s) — the UI re-renders the
 *     warning banner even if the child previously dismissed it.
 */
function computeSessionTimerPhase({
  startedAtMs,
  timeLimitMinutes,
  nowMs,
  backgroundMs = 0,
  graceSeconds = DEFAULT_GRACE_SECONDS,
  bannerReappearSeconds = DEFAULT_BANNER_REAPPEAR_SECONDS,
}) {
  const timeLimitSeconds = Math.max(0, Math.floor(timeLimitMinutes * 60));
  // Subtract backgrounded time from the elapsed window. The Math.max
  // floor handles two edge cases: a negative backgroundMs argument
  // (treat as zero), and a backgroundMs larger than the wall-clock
  // elapsed (returns 0 → active phase with the full allowance, never
  // wraps into a negative count).
  const safeBackgroundMs = Math.max(0, backgroundMs);
  const elapsedSeconds   = Math.max(0, Math.floor((nowMs - startedAtMs - safeBackgroundMs) / 1000));

  // Active phase — child sees the normal countdown.
  if (elapsedSeconds < timeLimitSeconds) {
    return {
      phase:               'active',
      secondsLeft:         timeLimitSeconds - elapsedSeconds,
      graceSecondsLeft:    graceSeconds,
      bannerForcedVisible: false,
    };
  }

  // Grace phase — timer reads 0, amber banner shows, child can still type.
  const graceElapsed = elapsedSeconds - timeLimitSeconds;
  if (graceElapsed < graceSeconds) {
    const graceRemaining = graceSeconds - graceElapsed;
    return {
      phase:               'grace',
      secondsLeft:         0,
      graceSecondsLeft:    graceRemaining,
      bannerForcedVisible: graceRemaining <= bannerReappearSeconds,
    };
  }

  // Expired — caller should seal the session.
  return {
    phase:               'expired',
    secondsLeft:         0,
    graceSecondsLeft:    0,
    bannerForcedVisible: false,
  };
}

module.exports = {
  computeSessionTimerPhase,
  DEFAULT_GRACE_SECONDS,
  DEFAULT_BANNER_REAPPEAR_SECONDS,
};
